import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { getWithTokenAPI } from "../../util/Api";
import {
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  Eye,
  X,
  User,
  Calendar,
  DollarSign,
  Shield,
  Clock,
  RefreshCw,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import Swal from "sweetalert2";
import { Link } from 'react-router-dom';

// Material-UI imports
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Checkbox,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useTranslation } from "react-i18next";

const PaymentList = () => {
  const [paymentList, setPaymentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    activeCount: 0,
    expiredCount: 0
  });

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    paymentStatus: "",
    subscriptionStatus: "",
    planName: "",
  });

  // Column visibility states
  const [columnVisibility, setColumnVisibility] = useState({
    user: true,
    mobile: true,
    planName: true,
    amount: true,
    paymentStatus: true,
    subscriptionStatus: true,
    startDate: true,
    endDate: true,
    actions: true,
  });

  const token = localStorage.getItem("matrimony_token");

  const getPaymentList = async (page = 1, search = "", filterParams = {}) => {
    try {
      setLoading(true);

      // Build query parameters
      let queryParams = `page=${page}&limit=10`;

      // Add search term if provided
      if (search) {
        queryParams += `&search=${encodeURIComponent(search)}`;
      }

      // Add filters if provided
      if (filterParams.paymentStatus) {
        queryParams += `&paymentStatus=${encodeURIComponent(filterParams.paymentStatus)}`;
      }
      if (filterParams.subscriptionStatus) {
        queryParams += `&subscriptionStatus=${encodeURIComponent(filterParams.subscriptionStatus)}`;
      }
      if (filterParams.planName) {
        queryParams += `&planName=${encodeURIComponent(filterParams.planName)}`;
      }

      const response = await getWithTokenAPI(token, `/userSubscription/get_all?${queryParams}`);
      console.log('getPaymentList', response);

      if (response.statusCode === 200) {
        setPaymentList(response.data || []);
        setPaginationInfo({
          currentPage: response.currentPage || 1,
          totalPages: response.totalPages || 1,
          totalCount: response.totalCount || 0,
          limit: response.limit || 10,
          activeCount: response.activeCount || 0,
          expiredCount: response.expiredCount || 0
        });
      } else {
        Swal.fire("Error", response.message || "Failed to fetch data", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPaymentList(1);
  }, []);

  // Handle server-side pagination
  const handleServerPageChange = (newPage) => {
    getPaymentList(newPage + 1, searchTerm, filters); // +1 because Material-UI pages are 0-indexed
  };

  const handleServerRowsPerPageChange = (event) => {
    // If your API supports changing page size, implement it here
    const newLimit = parseInt(event.target.value, 10);
    getPaymentList(1, searchTerm, filters); // Reset to first page with new limit
  };

  // Handle search with debouncing
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      getPaymentList(1, searchTerm, filters);
    }, 500); // 500ms debounce

    return () => clearTimeout(delaySearch);
  }, [searchTerm]);

  // Handle filter changes
  useEffect(() => {
    getPaymentList(1, searchTerm, filters);
  }, [filters]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const openModal = (payment) => {
    setSelectedPayment(payment);
    setIsModalOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setSelectedPayment(null);
    }, 300);
    document.body.style.overflow = "unset";
  };

  // Search and Filter handlers
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleColumnsClick = (event) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleColumnsClose = () => {
    setColumnsAnchorEl(null);
  };

  const handleColumnVisibilityChange = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      paymentStatus: "",
      subscriptionStatus: "",
      planName: "",
    });
    setSearchTerm("");
  };

  // Status badge colors
  const getStatusBadge = (status, type = 'payment') => {
    const config = {
      payment: {
        success: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200", label: "Success" },
        failed: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", label: "Failed" },
        pending: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-200", label: "Pending" },
      },
      subscription: {
        active: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", label: "Active" },
        expired: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", label: "Expired" },
        cancelled: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", label: "Cancelled" },
      }
    };

    const statusConfig = config[type][status?.toLowerCase()] || {
      bg: "bg-gray-100",
      text: "text-gray-700",
      border: "border-gray-200",
      label: status || "—"
    };

    return (
      <Badge className={`text-xs px-2 py-1 ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
        {statusConfig.label}
      </Badge>
    );
  };

  const { t } = useTranslation();
  // Stats cards data
  const statsCards = [
    { title: t("payment.Total"), value: paginationInfo.totalCount, icon: FileText, color: "blue" },
    { title: t("payment.Active"), value: paginationInfo.activeCount, icon: CheckCircle, color: "green" },
    { title: t("payment.Expired"), value: paginationInfo.expiredCount, icon: XCircle, color: "red" },
  ];

  const columns = [
    { key: 'user', label: t("payment.user"), visible: columnVisibility.user },
    { key: 'mobile', label: t("payment.mobile"), visible: columnVisibility.mobile },
    { key: 'planName', label: t("payment.planName"), visible: columnVisibility.planName },
    { key: 'amount', label: t("payment.amount"), visible: columnVisibility.amount },
    { key: 'paymentStatus', label: t("payment.paymentStatus"), visible: columnVisibility.paymentStatus },
    { key: 'subscriptionStatus', label: t("payment.subscriptionStatus"), visible: columnVisibility.subscriptionStatus },
    { key: 'startDate', label: t("payment.startDate"), visible: columnVisibility.startDate },
    { key: 'endDate', label: t("payment.endDate"), visible: columnVisibility.endDate },
    { key: 'actions', label: t("payment.actions"), visible: columnVisibility.actions },
  ];

  const visibleColumns = columns.filter(col => col.visible);

  // Custom Pagination Component for better server-side control
  const CustomPagination = () => (
    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
      <div className="flex items-center gap-2 text-sm text-gray-700">
        <span>Showing</span>
        <span className="font-medium">{(paginationInfo.currentPage - 1) * paginationInfo.limit + 1}</span>
        <span>to</span>
        <span className="font-medium">
          {Math.min(paginationInfo.currentPage * paginationInfo.limit, paginationInfo.totalCount)}
        </span>
        <span>of</span>
        <span className="font-medium">{paginationInfo.totalCount}</span>
        <span>results</span>
      </div>

      <div className="flex items-center gap-1">
        {/* First Page */}
        <button
          onClick={() => getPaymentList(1, searchTerm, filters)}
          disabled={paginationInfo.currentPage === 1}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 cursor-pointer"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Previous Page */}
        <button
          onClick={() => getPaymentList(paginationInfo.currentPage - 1, searchTerm, filters)}
          disabled={paginationInfo.currentPage === 1}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 cursor-pointer"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page Numbers */}
        <div className="flex items-center gap-1 mx-2">
          {Array.from({ length: Math.min(5, paginationInfo.totalPages) }, (_, i) => {
            let pageNum;
            if (paginationInfo.totalPages <= 5) {
              pageNum = i + 1;
            } else if (paginationInfo.currentPage <= 3) {
              pageNum = i + 1;
            } else if (paginationInfo.currentPage >= paginationInfo.totalPages - 2) {
              pageNum = paginationInfo.totalPages - 4 + i;
            } else {
              pageNum = paginationInfo.currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => getPaymentList(pageNum, searchTerm, filters)}
                className={`px-3 py-1 rounded text-sm font-medium cursor-pointer ${paginationInfo.currentPage === pageNum
                  ? 'bg-blue-600 text-white'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        {/* Next Page */}
        <button
          onClick={() => getPaymentList(paginationInfo.currentPage + 1, searchTerm, filters)}
          disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 cursor-pointer"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last Page */}
        <button
          onClick={() => getPaymentList(paginationInfo.totalPages, searchTerm, filters)}
          disabled={paginationInfo.currentPage === paginationInfo.totalPages}
          className="p-2 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 cursor-pointer"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-4 bg-gray-50 min-h-screen text-sm">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-bold text-gray-800">{t("payment.title")}</h1>
      </div>

      {/* Stats Section */}
      <div className="flex flex-wrap justify-between gap-3 mb-6">
        {statsCards.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card
              key={i}
              className="flex-1 min-w-[200px] bg-white border shadow-sm rounded-xl p-4 flex flex-row items-center justify-between hover:shadow-md transition-all"
            >
              <div className="flex flex-col">
                <p className="text-sm text-gray-600 font-medium">{s.title}</p>
                <p className={`font-bold text-${s.color}-600 text-xl mt-1`}>
                  {s.value}
                </p>
              </div>
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full bg-${s.color}-100 ml-3`}
              >
                <Icon className={`w-5 h-5 text-${s.color}-600`} />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Single Card for Search and Table */}
      <Card className="shadow-sm border">
        <CardContent className="p-0">
          {/* Search and Filter Section */}
          <div className="p-4 border-b border-gray-200 bg-white">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              {/* Search Bar */}
              <div className="flex-1 w-full sm:max-w-md">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("userManagement.searchPlaceholder")}
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon className="text-gray-400" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm("")}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="w-4 h-4" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      width: "400px"
                    }
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {/* Filter Button */}
                <Tooltip title="Filter">
                  <IconButton
                    onClick={handleFilterClick}
                    className={`border ${filters.paymentStatus || filters.subscriptionStatus || filters.planName ? 'bg-blue-50 border-blue-200' : 'border-gray-300'}`}
                  >
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>

                {/* Columns Button */}
                <Tooltip title="Show/Hide Columns">
                  <IconButton
                    onClick={handleColumnsClick}
                    className="border border-gray-300"
                  >
                    <ViewColumnIcon />
                  </IconButton>
                </Tooltip>

                {/* Refresh Button */}
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => getPaymentList(1, searchTerm, filters)}
                    className="border border-gray-300"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            {/* Active Filters Display */}
            {(filters.paymentStatus || filters.subscriptionStatus || filters.planName) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {filters.paymentStatus && (
                  <Badge className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                    Payment: {filters.paymentStatus}
                    <button onClick={() => handleFilterChange('paymentStatus', '')} className="ml-1">×</button>
                  </Badge>
                )}
                {filters.subscriptionStatus && (
                  <Badge className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                    Subscription: {filters.subscriptionStatus}
                    <button onClick={() => handleFilterChange('subscriptionStatus', '')} className="ml-1">×</button>
                  </Badge>
                )}
                {filters.planName && (
                  <Badge className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                    Plan: {filters.planName}
                    <button onClick={() => handleFilterChange('planName', '')} className="ml-1">×</button>
                  </Badge>
                )}
                <button
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-800 text-xs font-medium"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>

          {/* Table Section */}
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="animate-spin text-blue-600" />
            </div>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 650 }} size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: '600', fontSize: '0.875rem', py: 2, width: 80 }}>
                      {t("reports.sno")}
                    </TableCell>
                    {visibleColumns.map((column) => (
                      <TableCell
                        key={column.key}
                        sx={{ fontWeight: '600', fontSize: '0.875rem', py: 2 }}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length + 1} align="center" sx={{ py: 4 }}>
                        <div className="text-gray-500 text-sm">
                          {searchTerm || filters.paymentStatus || filters.subscriptionStatus || filters.planName
                            ? "No payments found for your search/filters"
                            : "No payments found"
                          }
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paymentList.map((payment, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { backgroundColor: '#f9fafb' }
                        }}
                      >
                        <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                          {(paginationInfo.currentPage - 1) * paginationInfo.limit + index + 1}
                        </TableCell>

                        {columnVisibility.user && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                            <Link
                              to={`/admin/user-profile/${payment?.user_id}`}
                              className="font-semibold text-gray-800 hover:text-blue-600 hover:underline cursor-pointer transition-colors duration-200 block text-blue-600"
                            >
                              {payment.user_profile?.name || "—"}
                            </Link>
                          </TableCell>
                        )}

                        {columnVisibility.mobile && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                            {payment.user_profile?.mobile_number || "—"}
                          </TableCell>
                        )}

                        {columnVisibility.planName && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2, fontWeight: 500 }}>
                            {payment.plan_details?.plan_name || "—"}
                          </TableCell>
                        )}

                        {columnVisibility.amount && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2, fontWeight: 600, color: '#059669' }}>
                            {formatCurrency(payment.plan_details?.price)}
                          </TableCell>
                        )}

                        {columnVisibility.paymentStatus && (
                          <TableCell sx={{ py: 2 }}>
                            {getStatusBadge(payment.payment_status, 'payment')}
                          </TableCell>
                        )}

                        {columnVisibility.subscriptionStatus && (
                          <TableCell sx={{ py: 2 }}>
                            {getStatusBadge(payment.status, 'subscription')}
                          </TableCell>
                        )}

                        {columnVisibility.startDate && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                            {formatDate(payment.start_date)}
                          </TableCell>
                        )}
                        {columnVisibility.endDate && (
                          <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                            {formatDate(payment.end_date)}
                          </TableCell>
                        )}

                        {columnVisibility.actions && (
                          <TableCell sx={{ py: 2 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                onClick={() => openModal(payment)}
                                size="small"
                                sx={{
                                  color: '#2563eb',
                                  '&:hover': {
                                    backgroundColor: 'rgba(37, 99, 235, 0.1)',
                                  }
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Custom Pagination */}
              <CustomPagination />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { width: 250, p: 2 }
        }}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
            <select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Status</label>
            <select
              value={filters.subscriptionStatus}
              onChange={(e) => handleFilterChange('subscriptionStatus', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Plan Name</label>
            <TextField
              size="small"
              fullWidth
              placeholder="Filter by plan..."
              value={filters.planName}
              onChange={(e) => handleFilterChange('planName', e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              inputProps={{
                onKeyDown: (e) => {
                  e.stopPropagation();
                }
              }}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={clearFilters}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 cursor-pointer"
            >
              Clear
            </button>
            <button
              onClick={handleFilterClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 cursor-pointer"
            >
              Apply
            </button>
          </div>
        </div>
      </Menu>

      {/* Columns Menu */}
      <Menu
        anchorEl={columnsAnchorEl}
        open={Boolean(columnsAnchorEl)}
        onClose={handleColumnsClose}
        PaperProps={{
          sx: { width: 220 }
        }}
      >
        <MenuItem disabled sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
          Show/Hide Columns
        </MenuItem>
        {columns.map((column) => (
          <MenuItem
            key={column.key}
            onClick={() => handleColumnVisibilityChange(column.key)}
            sx={{ fontSize: '0.875rem' }}
          >
            <Checkbox
              checked={column.visible}
              size="small"
              sx={{
                padding: '4px',
                '&.Mui-checked': {
                  color: '#2563eb',
                },
              }}
            />
            <span className="text-sm ml-2">{column.label}</span>
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            const allVisible = {};
            columns.forEach(col => {
              allVisible[col.key] = true;
            });
            setColumnVisibility(allVisible);
          }}
          sx={{
            fontSize: '0.875rem',
            borderTop: '1px solid #e5e7eb',
            marginTop: '4px',
            paddingTop: '8px'
          }}
        >
          <Checkbox
            checked={Object.values(columnVisibility).every(val => val)}
            size="small"
            sx={{
              padding: '4px',
              '&.Mui-checked': {
                color: '#2563eb',
              },
            }}
          />
          <span className="text-sm ml-2 font-medium text-blue-600">Show All Columns</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const allHidden = {};
            columns.forEach(col => {
              allHidden[col.key] = false;
            });
            setColumnVisibility(allHidden);
          }}
          sx={{ fontSize: '0.875rem' }}
        >
          <Checkbox
            checked={Object.values(columnVisibility).every(val => !val)}
            size="small"
            sx={{
              padding: '4px',
              '&.Mui-checked': {
                color: '#2563eb',
              },
            }}
          />
          <span className="text-sm ml-2 font-medium text-red-600">Hide All Columns</span>
        </MenuItem>
      </Menu>

      {/* Animated Right Side Popup Modal */}
      {selectedPayment && (
        <div className={`fixed inset-0 z-50 transition-all duration-300 ${isModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} >
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${isModalOpen ? 'opacity-50' : 'opacity-0'}`}
            onClick={closeModal}
          />

          <div
            className={`absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl transform overflow-y-auto transition-transform duration-300 ease-in-out ${isModalOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Payment Details</h2>
                  <p className="text-sm text-gray-600">Complete subscription information</p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto h-[calc(100vh-80px)]">
              <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {selectedPayment.user_profile?.name || "—"}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {selectedPayment.user_profile?.mobile_number || "—"}
                    </p>
                  </div>
                </div>
                <div className="flex justify-between gap-4">
                  <div className="flex-1 relative p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-blue-100 shadow-sm hover:shadow-md transition-all duration-300 group">
                    <div className="absolute top-3 right-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                      </div>
                    </div>
                    <div className="pr-10">
                      <h3 className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
                        Payment ID
                      </h3>
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-lg font-bold text-gray-900 break-all">
                          {selectedPayment.payment_id || "—"}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 relative p-4 bg-gradient-to-br from-slate-50 to-green-50 rounded-2xl border border-green-100 shadow-sm hover:shadow-md transition-all duration-300 group">
                    <div className="absolute top-3 right-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <ShoppingCart className="w-4 h-4 text-green-600" />
                      </div>
                    </div>
                    <div className="pr-10">
                      <h3 className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">
                        Order ID
                      </h3>
                      <div className="flex items-center gap-2">
                        <div className="font-mono text-lg font-bold text-gray-900 break-all">
                          {selectedPayment.order_id || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="w-4 h-4 text-purple-600" />
                    <h4 className="font-semibold text-gray-800">Plan Details</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan Name:</span>
                      <span className="font-medium text-gray-800">{selectedPayment.plan_details?.plan_name || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Plan ID:</span>
                      <span className="font-medium text-gray-800">{selectedPayment.plan_id || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Currency:</span>
                      <span className="font-medium text-gray-800">{selectedPayment.plan_details?.currency || "—"}</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <h4 className="font-semibold text-gray-800">Payment Details</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount:</span>
                      <span className="font-bold text-green-600 text-lg">
                        {formatCurrency(selectedPayment.plan_details?.price)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Status:</span>
                      {getStatusBadge(selectedPayment.payment_status, 'payment')}
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Auto Renew:</span>
                      <span className="font-medium text-gray-800">
                        {selectedPayment.auto_renew ? "Yes" : "No"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-gray-800">Timeline</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 block mb-1">Start Date:</span>
                    <div className="flex items-center gap-2 text-gray-800">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedPayment.start_date)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">End Date:</span>
                    <div className="flex items-center gap-2 text-gray-800">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedPayment.end_date)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Created Date:</span>
                    <div className="flex items-center gap-2 text-gray-800">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedPayment.createdAt)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500 block mb-1">Updated Date:</span>
                    <div className="flex items-center gap-2 text-gray-800">
                      <Calendar className="w-4 h-4" />
                      {formatDate(selectedPayment.updatedAt)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-blue-600" />
                  <h4 className="font-semibold text-gray-800">Subscription Status</h4>
                </div>
                <div className="flex items-center gap-4">
                  {getStatusBadge(selectedPayment.status, 'subscription')}
                  {selectedPayment.payment_message && (
                    <div className="flex-1">
                      <span className="text-gray-500 block mb-1 text-sm">Message:</span>
                      <p className="text-green-500 text-sm bg-gray-50 p-2 rounded">
                        {selectedPayment.payment_message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

          <div className="flex justify-end p-6 cursor-pointer">
            <Button
              variant="outline"
              onClick={closeModal}
            >
              Close
            </Button>
          </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentList;